import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { JobEntity, JobOperation } from '@commenda/api';
import {
  Badge,
  Button,
  Dropdown,
  DropdownMenuItemProps,
  EmptyState,
  HorizontalTabsList,
  Icons,
  LoadingIndicator,
  Tabs,
  TabsTrigger,
  useToggle,
} from '@commenda/kakashi';

import { FilterTabs } from '../../../components';
import { useNotification } from '../../../hooks/useNotification';
import {
  IndirectTaxDataSyncBanner,
  IndirectTaxHelper,
  IndirectTaxJobsLogDialog,
  IndirectTaxRegion,
} from '../../common';
import { IndirectTaxClientTaskBar } from '../../common/components';
import { IndirectTaxFilingCalendarPreviewModal } from '../calendar-preview';
import {
  IndirectTaxFilingsBulkActionBar,
  IndirectTaxFilingsEmptyState,
  IndirectTaxFilingSettingsModal,
  IndirectTaxFilingsStatusModal,
  IndirectTaxFilingsTable,
  IndirectTaxTaxLiabilityReportModal,
} from '../components';
import { IndirectTaxFilingApproveModal } from '../detail/IndirectTaxFilingApproveModal';
import { IndirectTaxFilingsHelper } from '../helpers';
import { IIndirectTaxFiling, IndirectTaxFilingStatus } from '../types';
import { IndirectTaxFilingsPeriodPicker } from './IndirectTaxFilingsPeriodPicker';

import { TaskParty } from '@commenda/prisma';

import { useIndirectTaxFilingAccess } from '../../common/hooks/useIndirectTaxFilingAccess';
import { useIndirectTaxFilingsDashboard } from './useIndirectTaxFilingsDashboard';

interface IndirectTaxFilingsDashboardProps {
  header: React.ReactNode;
  corporationId: number;
  onFilingClick?: (filing: IIndirectTaxFiling) => void;
  taskAssigneeType?: TaskParty;
  showClientTaskBar?: boolean;
  canApproveFilings?: boolean;
  showFiledBy?: boolean;
}

const IndirectTaxFilingsDashboard = ({
  header,
  corporationId,
  onFilingClick,
  taskAssigneeType = TaskParty.CLIENT,
  showClientTaskBar = false,
  canApproveFilings = false,
  showFiledBy = false,
}: IndirectTaxFilingsDashboardProps) => {
  const {
    isRegionUnavailable,
    activeRegion,
    activeFilingStatusTab,
    activeFilingFilters,
    filingPeriod,
    filings,
    regionJurisdictions,
    totalFilingCount,
    statusCounts,
    hasFilings,
    hasRegistrations,
    isLoadingFilings,
    filingsError,
    isFilingJobInProgress,
    clientTasks,
    aggregate,
    aggregateError,
    handleFilingRegionChange,
    handleFilingStatusTabChange,
    handleFilingFilterChange,
    handleFilingPeriodChange,
    handleNextPage,
    handlePreviousPage,
    hasNextPage,
    hasPreviousPage,
    currentPage,
    pageSize,
    getJobsByEntityAndOperation,
    mutateFilings,
    retryFilings,
  } = useIndirectTaxFilingsDashboard({ corporationId, taskAssigneeType });

  const { isCalendarOnly, isInternalUser: isCommendaUser } =
    useIndirectTaxFilingAccess(activeRegion);

  const [isRetrying, setIsRetrying] = useState(false);
  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    try {
      // Hard refresh: drop the cached error and force a refetch.
      await retryFilings();
    } finally {
      setIsRetrying(false);
    }
  }, [retryFilings]);

  const { setNotification } = useNotification();

  const showNotification = useCallback(
    (valid: boolean, text: string) => {
      setNotification({
        show: true,
        valid,
        title: text,
      });
    },
    [setNotification]
  );

  // Surface aggregate failures (multi-region 400s, network errors, etc.)
  // as a non-blocking toast so the table can keep rendering while the
  // summary cards drop to dashes. Per the API standards: every SWR error
  // must be handled, not swallowed.
  //
  // Debounced because switching region fires a burst of param changes
  // (region + status/filter resets, then jurisdictionIds settling a render
  // later), so the aggregate key changes 2-3 times and each failed fetch
  // yields a distinct error — un-debounced that's 2-3 identical toasts (and
  // StrictMode doubles it in dev). Each error reschedules the timer, so only
  // the final settled error fires one toast; if the error clears first
  // (recovery / switch to a working region) the cleanup cancels it.
  useEffect(() => {
    if (!aggregateError) return undefined;
    // Show a short, user-facing message. The precise backend reason (e.g.
    // the multi-region jurisdiction detail) stays on `aggregateError` for
    // logging — it's too technical to surface in a toast.
    const timeoutId = setTimeout(
      () => showNotification(false, 'Failed to load filings summary.'),
      300
    );
    return () => clearTimeout(timeoutId);
  }, [aggregateError, showNotification]);

  const exportJobTableVisibility = useToggle();
  const approveModal = useToggle();
  const [selectedFilingForApproval, setSelectedFilingForApproval] =
    useState<IIndirectTaxFiling | null>(null);
  const settingsModal = useToggle();

  const handleApproveClick = useCallback(
    (filing: IIndirectTaxFiling) => {
      setSelectedFilingForApproval(filing);
      approveModal.show();
    },
    [approveModal]
  );

  const [selectedFilings, setSelectedFilings] = useState<IIndirectTaxFiling[]>(
    []
  );
  const selectedFilingIds = useMemo(
    () => new Set(selectedFilings.map((f) => f.id)),
    [selectedFilings]
  );
  const taxLiabilityModal = useToggle();
  const filingsStatusModal = useToggle();
  const calendarPreviewModal = useToggle();

  const getDropdownOptions = (): DropdownMenuItemProps[] => {
    const dropdownOptions: DropdownMenuItemProps[] = isCalendarOnly
      ? []
      : [
          {
            icon: Icons.BarChart01,
            label: 'View tax liability report',
            onClick: taxLiabilityModal.show,
          },
          {
            icon: Icons.Activity,
            label: 'View tax liability report status',
            onClick: filingsStatusModal.show,
          },
        ];
    dropdownOptions.push({
      icon: Icons.Settings01,
      label: 'Settings',
      onClick: settingsModal.show,
    });

    if (isCommendaUser) {
      dropdownOptions.push({
        icon: Icons.Calendar,
        label: 'Generate filing calendar',
        shortcut: (
          <Badge variant="gray" size="sm">
            Internal
          </Badge>
        ),
        onClick: calendarPreviewModal.show,
      });
    }

    return dropdownOptions;
  };

  const filingStatusFilterTabs = useMemo(
    () => IndirectTaxFilingsHelper.getStatusFilterTabs(statusCounts),
    [statusCounts]
  );

  const handleFilingStatusTabChangeTyped = useCallback(
    (tab: string) => {
      // FilterTabs strips our typed enum to string at the callback boundary;
      // the tab id is always a valid IndirectTaxFilingStatus or 'ALL' because
      // we built the tab list from those exact values above.
      handleFilingStatusTabChange(tab as IndirectTaxFilingStatus | 'ALL');
    },
    [handleFilingStatusTabChange]
  );

  const handleRegionTabChange = useCallback(
    (value: string) => {
      // Tabs value is built from IndirectTaxRegion enum members; the cast is
      // safe because no caller can inject an out-of-set value here.
      handleFilingRegionChange(value as IndirectTaxRegion);
    },
    [handleFilingRegionChange]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header with dropdown — always rendered. "Generate filing calendar"
          is specifically useful in the zero-filings-yet state, so gating the
          menu behind hasFilings would hide the primary action users need when
          they land on the page for the first time. */}
      <div className="flex items-center justify-between p-3xl border-b border-border-primary">
        <div className="flex-1">{header}</div>

        <Dropdown
          menu={{
            groups: [
              {
                items: getDropdownOptions(),
              },
            ],
          }}
          align="end"
          contentClassName="w-72"
        >
          <Button variant="tertiary" size="sm">
            <Icons.DotsVertical className="size-4" />
          </Button>
        </Dropdown>
      </div>

      {hasFilings && showClientTaskBar && !isCalendarOnly && (
        <div data-ai-section="Filing tasks">
          <IndirectTaxClientTaskBar
            tasks={clientTasks}
            corporationId={corporationId}
          />
        </div>
      )}

      {/* Due in period selector */}
      <div
        className="flex items-center gap-md px-3xl pb-3xl pt-3xl"
        data-ai-section="Due in period selector"
      >
        <span className="txt-medium-bold text-text-primary">Due in</span>
        <IndirectTaxFilingsPeriodPicker
          fromDate={filingPeriod.fromDate}
          toDate={filingPeriod.toDate}
          onChange={handleFilingPeriodChange}
        />
      </div>

      {/* Region tabs */}
      <div
        className="border-b border-border-primary px-3xl"
        data-ai-section="Region tabs"
      >
        <Tabs value={activeRegion} onValueChange={handleRegionTabChange}>
          <HorizontalTabsList variant="underline" className="border-b-0 gap-lg">
            {IndirectTaxHelper.REGION_TABS.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </HorizontalTabsList>
        </Tabs>
      </div>

      {isFilingJobInProgress && !isCalendarOnly && (
        <IndirectTaxDataSyncBanner
          corporationId={corporationId}
          entity={JobEntity.FILING}
        />
      )}

      {isRegionUnavailable || (filingsError && filings.length === 0) ? (
        <EmptyState
          heading="Failed to load filings"
          description="Please try again or view all regions."
          primaryAction={{
            label: 'Retry',
            onClick: handleRetry,
            variant: 'secondary',
            isLoading: isRetrying,
          }}
          secondaryAction={{
            label: 'View all regions',
            onClick: () =>
              handleFilingRegionChange(IndirectTaxRegion.ALL_REGIONS),
            variant: 'secondary',
          }}
        />
      ) : isLoadingFilings && filings.length === 0 ? (
        <div
          className="flex items-center justify-center p-3xl"
          role="status"
          aria-label="Loading filings"
        >
          <LoadingIndicator />
        </div>
      ) : hasFilings ? (
        <>
          {/* Status tabs */}
          <div className="p-3xl" data-ai-section="Filing status tabs">
            <FilterTabs
              tabs={filingStatusFilterTabs}
              activeTab={activeFilingStatusTab}
              onTabChange={handleFilingStatusTabChangeTyped}
            />
          </div>

          {/* Table */}
          <div className="p-3xl" data-ai-section="Filings table">
            <IndirectTaxFilingsTable
              filings={filings}
              activeRegion={activeRegion}
              activeFilters={activeFilingFilters}
              onFilterChange={handleFilingFilterChange}
              regionJurisdictions={regionJurisdictions}
              pagination={{
                currentPage,
                pageSize,
                totalItems: totalFilingCount,
                hasNextPage,
                hasPreviousPage,
                onNextPage: handleNextPage,
                onPreviousPage: handlePreviousPage,
              }}
              onRowClick={onFilingClick}
              onApproveClick={
                canApproveFilings ? handleApproveClick : undefined
              }
              selectable={!isCalendarOnly}
              selectedFilingIds={selectedFilingIds}
              onSelectionChange={setSelectedFilings}
              aggregate={aggregate}
              showFiledBy={showFiledBy}
            />
          </div>
        </>
      ) : (
        <IndirectTaxFilingsEmptyState
          hasRegistrations={hasRegistrations}
          onGenerateCalendar={calendarPreviewModal.show}
        />
      )}

      <IndirectTaxJobsLogDialog
        isOpen={exportJobTableVisibility.visible}
        onClose={exportJobTableVisibility.hide}
        jobs={getJobsByEntityAndOperation(
          JobEntity.FILING,
          JobOperation.EXPORT_FILING_REPORT_FOR_CORPORATION
        )}
        corporationId={corporationId}
        jobOperation={JobOperation.EXPORT_FILING_REPORT_FOR_CORPORATION}
        title="Filing reports"
      />

      <IndirectTaxFilingSettingsModal
        isOpen={settingsModal.visible}
        onClose={settingsModal.hide}
        onUpdateSettings={() => mutateFilings?.()}
        corporationId={corporationId}
        onSuccess={(msg) => showNotification(true, msg)}
        onError={(msg) => showNotification(false, msg)}
      />

      <IndirectTaxTaxLiabilityReportModal
        isOpen={taxLiabilityModal.visible}
        onClose={taxLiabilityModal.hide}
        corporationId={corporationId}
        filings={filings}
        periodLabel={`${filingPeriod.fromDate} — ${filingPeriod.toDate}`}
      />

      <IndirectTaxFilingsStatusModal
        isOpen={filingsStatusModal.visible}
        onClose={filingsStatusModal.hide}
        corporationId={corporationId}
        filings={filings}
        onFilingClick={
          onFilingClick
            ? (filingId) => {
                const filing = filings.find((f) => f.id === filingId);
                if (filing) onFilingClick(filing);
              }
            : undefined
        }
      />

      <IndirectTaxFilingCalendarPreviewModal
        isOpen={calendarPreviewModal.visible}
        onClose={calendarPreviewModal.hide}
        corporationId={corporationId}
        onSuccess={() => {
          mutateFilings?.();
          showNotification(true, 'Filing calendar generated');
        }}
      />

      <IndirectTaxFilingApproveModal
        isOpen={approveModal.visible}
        onClose={approveModal.hide}
        filingId={selectedFilingForApproval?.id ?? ''}
        corporationId={corporationId}
        onApproved={() => {
          mutateFilings?.();
          showNotification(true, 'Filing approved');
        }}
      />

      <IndirectTaxFilingsBulkActionBar
        selectedFilings={selectedFilings}
        corporationId={corporationId}
        taskAssigneeType={taskAssigneeType}
        onClear={() => setSelectedFilings([])}
        onApprove={
          canApproveFilings
            ? () => {
                mutateFilings?.();
                showNotification(true, 'Filings approved');
                setSelectedFilings([]);
              }
            : undefined
        }
        onReview={() => {
          mutateFilings?.();
          showNotification(true, 'Filings marked as reviewed');
          setSelectedFilings([]);
        }}
        onApprovalMailSent={(skippedCount) => {
          const skippedMessage =
            skippedCount > 0
              ? ` ${skippedCount} filing(s) were skipped because they are no longer awaiting approval.`
              : '';
          showNotification(true, `Approval request sent.${skippedMessage}`);
          setSelectedFilings([]);
        }}
        onApprovalMailError={(message) => showNotification(false, message)}
      />
    </div>
  );
};

export { IndirectTaxFilingsDashboard };
